"use client";

import { useState } from "react";

import { useDiscoverContext } from "@/components/app/discover/discover-context";
import { EmptyState } from "@/components/shared/empty-state";
import { cn } from "@/utils/lib";

import { DiscoverMainFilter } from "../../../discover/discover-main-filter";
import { BaseTile } from "../base-tile";
import type { useTokenMetrics } from "../use-token-metrics";
import { UpcomingTokenDetails } from "./upcoming-token-details";
import { UpcomingTokenExtraContent } from "./upcoming-token-extra-content";

export const UpcomingTokensTab = ({
  hideFilters,
  hideResults,
  selectInitial = true,
  clickable = true,
  onSelect,
}: {
  hideFilters?: boolean;
  hideResults?: boolean;
  selectInitial?: boolean;
  clickable?: boolean;
  onSelect?: (selectedTokenMetrics: ReturnType<typeof useTokenMetrics>) => void;
}) => {
  const {
    creators,
    pinnedCreators,
    lazyLoadingMarkup,
    loading,
    initialLoading,
    freshCreatorStatPublicKeys,
  } = useDiscoverContext();
  const [selectedTokenMetrics, setSelectedTokenMetrics] = useState<ReturnType<
    typeof useTokenMetrics
  > | null>(null);

  const hasResults = creators.length > 0;

  return (
    <>
      {!hideFilters && (
        <div className="mt-4 flex size-full border-y border-border-light px-2">
          <DiscoverMainFilter isTimePeriodDisabled />
        </div>
      )}
      <div className="relative flex h-full min-h-lvh flex-row">
        <div
          className={cn("flex w-full flex-col gap-6 p-6 py-6", {
            "border-r border-border-light sm:w-7/12":
              hasResults && !hideResults,
          })}
        >
          {!initialLoading &&
            [...pinnedCreators, ...creators].map((creator, index) => {
              const finalPublicKey = creator.publicKeyBase58Check ?? "";

              const isFreshItem =
                freshCreatorStatPublicKeys.includes(finalPublicKey);

              return (
                <BaseTile
                  key={finalPublicKey}
                  index={index}
                  selectedTokenMetrics={selectedTokenMetrics}
                  onClick={(x) => {
                    setSelectedTokenMetrics(x);
                    onSelect?.(x);
                  }}
                  creator={creator}
                  isSelected={
                    selectedTokenMetrics?.selectedPublicKey === finalPublicKey
                  }
                  extraContent={
                    <UpcomingTokenExtraContent publicKey={finalPublicKey} />
                  }
                  className={isFreshItem ? "shake-animation" : ""}
                  selectInitial={selectInitial}
                  clickable={clickable}
                />
              );
            })}
          {lazyLoadingMarkup}
          {!hasResults && !loading && !initialLoading && (
            <div className="py-24">
              <EmptyState
                image="/assets/empty-15.png"
                title="We couldn't find any token."
                description="You might want to adjust your filters."
              />
            </div>
          )}
        </div>
        {hasResults && !hideResults && selectedTokenMetrics && (
          <UpcomingTokenDetails metrics={selectedTokenMetrics} />
        )}
      </div>
    </>
  );
};
